'use client'

import { onboardingStore } from '@components/onboarding/store'
import {
  LinkedAccountWithMetadata,
  useConnectWallet,
  useFundWallet,
  useLinkAccount,
  useLinkWithPasskey,
  useLogin,
  useLogout,
  usePrivy,
  User,
  useWallets
} from '@privy-io/react-auth'
import { useSetActiveWallet } from '@privy-io/wagmi'
import { env } from 'environments'
import { signIn, signOut, useSession } from 'next-auth/react'
import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useWalletClient } from 'wagmi'
import { useRouter, usePathname } from 'next/navigation'
import { userProfileRoute } from '@utils/routes'
import { useAuthenticatedWalletClient } from '@/hooks/use-authenticated-wallet-client'
import { useAnalyticsIdentify } from '@/hooks/use-analytics-identify'

interface AuthContextValue {
  login: ReturnType<typeof useLogin>['login']
  logout: ReturnType<typeof useLogout>['logout']
  linkEmail: ReturnType<typeof useLinkAccount>['linkEmail']
  linkWallet: ReturnType<typeof useLinkAccount>['linkWallet']
  linkWithPasskey: () => Promise<void>
  fundWallet: ReturnType<typeof useFundWallet>['fundWallet']
  unlinkEmail: (address: string) => Promise<User>
  unlinkWallet: (address: string) => Promise<User>
  unlinkPasskey: (credentialId: string) => Promise<User>
  user: ReturnType<typeof usePrivy>['user']
  status: 'loading' | 'authenticated' | 'unauthenticated' | 'authenticating'
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function ArtBlocksAuthProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const { data: session, status: sessionStatus, update: updateSession } = useSession()
  const { data: walletClient } = useWalletClient()
  const { wallets } = useWallets()
  const router = useRouter()
  const pathname = usePathname()

  const {
    getAccessToken,
    unlinkEmail: privyUnlinkEmail,
    unlinkWallet: privyUnlinkWallet,
    unlinkPasskey: privyUnlinkPasskey,
    ready: privyReady,
    authenticated: privyAuthenticated,
    user: privyUser,
    isModalOpen
  } = usePrivy()

  // Track user identification events with RudderStack
  useAnalyticsIdentify()

  // Set up privy link email account and update user profile on success
  const { linkEmail: privyLinkEmail, linkWallet: privyLinkWallet } = useLinkAccount({
    onSuccess: async ({ linkedAccount }) => {
      // update active wallet to the newly linked wallet and sync user session
      const connectedWallet = wallets?.find((wallet) => {
        if (linkedAccount.type === 'wallet' || linkedAccount.type === 'smart_wallet') {
          return wallet.address.toLowerCase() === linkedAccount.address.toLowerCase()
        }

        return false
      })

      if (connectedWallet) {
        setActiveWallet(connectedWallet)
        const accessToken = await getAccessToken()
        if (!accessToken) {
          console.error('missing required data to sync linked account', { accessToken })
          throw new Error('missing required data to sync linked account')
        }

        try {
          await signInWithNextAuth({ privyAccessToken: accessToken, publicAddress: connectedWallet.address })
        } catch (err) {
          console.error('signInWithNextAuth after linking account failed', err)
        }
      }
    },
    onError(err) {
      console.error('privy link account failed', err)
    }
  })
  // Set up privy fund wallet
  const { fundWallet: privyFundWallet } = useFundWallet()
  // Set up privy link passkey
  const { linkWithPasskey: privyLinkWithPasskey } = useLinkWithPasskey()
  // Set up privy login with next auth sign in on complete
  const [loginAccount, setLoginAccount] = useState<LinkedAccountWithMetadata | null>(null)

  // Setup privy login with callbacks to handling login state
  const { login: privyLogin } = useLogin({
    onComplete: async (params) => {
      // callback fires intermittently
      // we only need to sign in if the user is not already authenticated with both privy and next auth
      if (params.wasAlreadyAuthenticated && sessionStatus === 'authenticated') {
        return
      }

      const accessToken = await getAccessToken()
      const userAddress =
        params?.loginAccount?.type === 'wallet' ? params.loginAccount.address : params.user.wallet?.address

      if (!accessToken || !userAddress) {
        console.error('missing required data for login', { accessToken, userAddress })
        throw new Error('missing required data for login')
      }

      try {
        await signInWithNextAuth({ privyAccessToken: accessToken, publicAddress: userAddress })

        // otherwise login should be successful
        setLoginAccount(params.loginAccount)
      } catch (err) {
        console.error('signInWithNextAuth failed', err)

        // clear privy login state to prevent getting stuck in a half authed state
        privyLogout()
      }
    },
    onError(err) {
      console.error('privyLogin failed', err)
    }
  })

  // Set up privy logout with next auth sign out on success
  const { logout: privyLogout } = useLogout({
    onSuccess: () => {
      signOut({
        redirect: false
      })
      onboardingStore.trigger.reset()
    }
  })

  // custom logout function that redirects to profile page if user is on settings page
  const logout = useCallback(async () => {
    // capture data before logout starts (user data gets cleared during logout)
    const currentPathname = pathname
    const isOnSettingsPage = currentPathname.startsWith('/profile/settings')
    const userWalletAddress =
      privyUser?.wallet?.address || privyUser?.linkedAccounts?.find((account) => account.type === 'wallet')?.address

    // if user is on settings page, perform logout then redirect
    if (isOnSettingsPage && userWalletAddress) {
      await privyLogout()
      // redirect after logout completes
      router.push(userProfileRoute(userWalletAddress))
    } else {
      // normal logout for non-settings pages
      await privyLogout()
    }
  }, [privyLogout, pathname, privyUser, router])

  // Sync wagmi and privy wallets
  const { setActiveWallet } = useSetActiveWallet()

  useEffect(() => {
    // If the user logged in with a wallet, that should automatically be
    // the active wallet and we don't need to do anything but switch to the
    // correct chain. If the user logged in with an email, we need to find the
    // the user's first embedded wallet and make that the active wallet.
    const targetWallet = wallets.find((wallet) => {
      if (loginAccount?.type === 'wallet') {
        return wallet.linked && wallet.address.toLowerCase() === loginAccount.address.toLowerCase()
      }

      return wallet.linked && wallet.connectorType === 'embedded'
    })

    if (targetWallet && targetWallet.connectorType === 'embedded') {
      setActiveWallet(targetWallet).catch(console.error)
    }

    targetWallet?.switchChain(env.chain.id)
  }, [wallets, setActiveWallet, loginAccount])

  // Force logout if the user is authenticated through next auth but not privy
  useEffect(() => {
    if (privyReady && !privyAuthenticated && sessionStatus === 'authenticated') {
      const timeout = setTimeout(() => {
        console.log('force logout')
        signOut({
          redirect: false
        })
      }, 1000)
      return () => clearTimeout(timeout)
    }
  }, [
    privyReady,
    privyAuthenticated,
    sessionStatus,
    walletClient?.account?.address,
    privyUser?.linkedAccounts,
    privyLogout
  ])

  // Sync session address with active wallet address
  const authenticatedWalletClient = useAuthenticatedWalletClient()
  useEffect(() => {
    if (
      !authenticatedWalletClient?.account ||
      !session ||
      session.address?.toLowerCase() === authenticatedWalletClient.account.address.toLowerCase()
    ) {
      return
    }

    updateSession({
      ...session,
      address: authenticatedWalletClient.account.address.toLowerCase()
    })
  }, [authenticatedWalletClient, updateSession, session])

  // Force logout if the connected wallet is not linked to the user
  useEffect(() => {
    if (!walletClient?.account || !privyUser) {
      return
    }

    // This will be true if the user is in the middle of linking a wallet.
    // We don't want to force logout when the user connects their wallet
    // for linking.
    if (isModalOpen) {
      return
    }

    const isConnectedWalletLinked = privyUser.linkedAccounts.some(
      (account) =>
        account.type === 'wallet' && account.address.toLowerCase() === walletClient.account.address.toLowerCase()
    )

    if (!isConnectedWalletLinked) {
      logout()
    }
  }, [walletClient, privyUser, logout, isModalOpen])

  // Derive status from privy and session status
  let status: 'loading' | 'authenticated' | 'unauthenticated' | 'authenticating' = 'loading'
  if (privyReady) {
    // privy authenticated
    if (privyAuthenticated) {
      // privy authed and next auth authed - we are authed
      if (sessionStatus === 'authenticated') {
        status = 'authenticated'
      }
      // privy authed but next auth not authed yet - probably means login is in progress
      if (sessionStatus === 'unauthenticated') {
        status = 'authenticating'
      }
    }
    // if privy is not authed then we are not authenticated
    else {
      status = 'unauthenticated'
    }
  }

  return (
    <AuthContext.Provider
      value={{
        login: privyLogin,
        logout: logout,
        linkEmail: privyLinkEmail,
        linkWallet: privyLinkWallet,
        linkWithPasskey: privyLinkWithPasskey,
        fundWallet: privyFundWallet,
        unlinkEmail: privyUnlinkEmail,
        unlinkWallet: privyUnlinkWallet,
        unlinkPasskey: privyUnlinkPasskey,
        user: privyUser,
        status
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}

export function useLoginOrConnectWallet(): {
  loginOrConnectWallet: () => Promise<void>
} {
  const { login, status } = useAuth()
  const { connectWallet } = useConnectWallet()
  const { data: session } = useSession()

  return {
    loginOrConnectWallet: async () => {
      if (status === 'unauthenticated') {
        login?.()
      } else {
        connectWallet({ suggestedAddress: session?.address })
      }
    }
  }
}

/**
 * Sign in with NextAuth using the Privy access token and public address
 *
 * If this promise resolves it succeeded otherwise an error is thrown
 */
async function signInWithNextAuth({
  privyAccessToken,
  publicAddress
}: {
  privyAccessToken: string
  publicAddress: string
}): Promise<void> {
  const result = await signIn('privy', {
    accessToken: privyAccessToken,
    publicAddress,
    redirect: false
  })

  // check the result for errors
  if (result?.error) {
    console.error(`signIn failed - error: ${result?.error}`)
    throw new Error('signIn failed')
  }
}
